import React, { useState, useEffect } from "react";
import api, { setToken } from "../api";
import {
  ArrowPathIcon,
  BanknotesIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ExclamationCircleIcon,
  ArrowRightIcon,
  ClockIcon,
  XMarkIcon,
  InformationCircleIcon
} from "@heroicons/react/24/solid";
import { useNavigate } from 'react-router-dom';

export default function Swap() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [walletAddress, setWalletAddress] = useState("");
  const [chipAmount, setChipAmount] = useState("");
  const [usdtAmount, setUsdtAmount] = useState("0");
  const [copied, setCopied] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showStatusPopup, setShowStatusPopup] = useState(false);
  const [userSwaps, setUserSwaps] = useState([]);
  const [loadingSwaps, setLoadingSwaps] = useState(false);
  const [eligibleForWithdrawal, setEligibleForWithdrawal] = useState(false);
  const [checkingEligibility, setCheckingEligibility] = useState(true);
  
  // Constants for conversion rate
  const CHIP_VALUE_USD = 0.001; // $0.001 per chip

  const navigate = useNavigate();

  // Function to load user's swap requests
  const loadUserSwaps = async () => {
    setLoadingSwaps(true);
    try {
      const { data } = await api.get('/user-swaps');
      console.log("API response from user-swaps:", data);
      setUserSwaps(data.swaps || []);
    } catch (err) {
      console.error('Failed to load user swaps:', err);
    } finally {
      setLoadingSwaps(false);
    }
  };

  // Function to check if user is eligible for withdrawal
  const checkWithdrawalEligibility = async () => {
    setCheckingEligibility(true);
    try {
      const { data } = await api.get('/check-withdrawal-eligibility');
      console.log("API response from check-withdrawal-eligibility:", data);
      setEligibleForWithdrawal(data.eligible);
      if (!data.eligible && !error) {
        setError("You need to purchase chips at least once to be eligible for withdrawal.");
      }
    } catch (err) {
      console.error('Failed to check withdrawal eligibility:', err);
      setEligibleForWithdrawal(false);
      if (!error) {
        setError("Failed to verify withdrawal eligibility. Please try again later.");
      }
    } finally {
      setCheckingEligibility(false);
    }
  };

  // Calculate USDT amount based on chip amount
  useEffect(() => {
    if (!chipAmount || isNaN(chipAmount) || chipAmount <= 0) {
      setUsdtAmount("0");
      return;
    }
    
    const estimatedUsdtAmount = chipAmount * CHIP_VALUE_USD;
    setUsdtAmount(estimatedUsdtAmount.toFixed(3));
  }, [chipAmount]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) setToken(token);
  
    // Initial profile data fetch
    api.get("/profile")
      .then(({ data }) => {
        console.log("→ [Swap] got profile data:", data);
        setProfile(data);
        
        // Check if user has purchased chips by looking at both sources
        const hasProcessedTransactions = data.processedTransactions && data.processedTransactions.length > 0;
        const hasPurchaseActivity = data.recentActivity && 
                                data.recentActivity.some(activity => activity.type === 'purchase');
        
        // User is eligible if either condition is true
        const isEligible = hasProcessedTransactions || hasPurchaseActivity;
        setEligibleForWithdrawal(isEligible);
        
        if (!isEligible) {
          setError("You need to purchase chips at least once to be eligible for withdrawal.");
        }
      })
      .catch(err => {
        console.error("Failed to load profile:", err);
        setError("Failed to load your profile data. Please try again.");
      })
      .finally(() => {
        setLoading(false);
        setCheckingEligibility(false);
      });
  }, []);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const validateForm = () => {
    // Reset error and success messages
    setError("");
    setSuccess("");
    
    // Check eligibility first
    if (!eligibleForWithdrawal) {
      setError("You need to purchase chips at least once to be eligible for withdrawal.");
      return false;
    }
    
    // Validate chip amount
    if (!chipAmount || isNaN(chipAmount) || chipAmount <= 0) {
      setError("Please enter a valid chip amount.");
      return false;
    }
    
    // Check if user has enough chips
    if (profile && chipAmount > profile.chips) {
      setError("You don't have enough chips for this swap.");
      return false;
    }
    
    // Check minimum amount
    if (chipAmount < 10000) {
      setError("Minimum swap amount is 10000 chips.");
      return false;
    }
    
    // Validate wallet address
    if (!walletAddress || walletAddress.trim() === "") {
      setError("Please enter your wallet address.");
      return false;
    }
    
    // Validate USDT-compatible address format (basic check)
    if (walletAddress.length !== 44) {
      setError("Please enter a valid wallet address for USDT (SOL).");
      return false;
    }
    
    return true;
  };

  const handleSwap = async () => {
    if (!validateForm()) return;
    
    setIsProcessing(true);
    setError("");
    setSuccess("");
    
    try {
      console.log("Sending swap request with data:", {
        chipAmount: Number(chipAmount),
        walletAddress,
        pumpkinValueUsd: 1, // Used in your schema, not usdtAmount
        pumpkinAmount: Number(usdtAmount)
      });
      
      const response = await api.post("/swap", {
        chipAmount: Number(chipAmount),
        walletAddress,
        pumpkinValueUsd: 1, // Use 1 as placeholder since we're using fixed rate
        pumpkinAmount: Number(usdtAmount) // This matches your schema
      });
      
      console.log("→ [Swap] swap response:", response.data);
      
      setSuccess(`Success! ${chipAmount} chips have been swapped for ${usdtAmount} USDT. Tokens will be sent to your wallet shortly.`);
      setChipAmount("");
      setWalletAddress("");
      
      // Update the user's chip balance
      if (profile) {
        setProfile({
          ...profile,
          chips: profile.chips - Number(chipAmount)
        });
      }
      
      // Load the user's swap requests and show the status popup
      await loadUserSwaps();
      setShowStatusPopup(true);
    } catch (err) {
      console.error("Swap failed:", err);
      if (err.response) {
        setError(err.response.data?.message || `Server error: ${err.response.status}`);
      } else if (err.request) {
        setError("No response from server. Please check your connection and try again.");
      } else {
        setError(`Request error: ${err.message}`);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleMaxClick = () => {
    if (profile && profile.chips > 0) {
      setChipAmount(profile.chips.toString());
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-black text-gray-300">
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 border-2 border-violet-500 rounded-full relative flex items-center justify-center">
            <div className="w-10 h-10 bg-gradient-to-r from-violet-500 via-pink-500 to-red-500 rounded-full animate-pulse" />
            <div className="absolute inset-0 border-t-2 border-violet-500 rounded-full animate-spin" />
          </div>
          <div className="mt-4 font-mono text-sm">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
              Loading Swap...
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-gradient-to-br from-gray-900 to-black -mt-16 pt-4 pb-12 px-2 sm:px-4 text-white font-mono">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <h1 className="text-2xl sm:text-3xl lg:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-violet-500 via-pink-500 to-red-500 text-center mb-6 sm:mb-8">
          Swap Chips
        </h1>

        {/* Eligibility Notice */}
        {!eligibleForWithdrawal && !checkingEligibility && (
          <div className="mb-4 p-3 sm:p-4 bg-yellow-950 border border-yellow-500 rounded-lg flex items-start">
            <InformationCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-500 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm sm:text-base text-yellow-200 font-bold">Withdrawal Not Available</p>
              <p className="text-xs sm:text-sm text-yellow-300 mt-1">
                You need to purchase chips at least once to withdraw. Visit the Store to buy chips and unlock withdrawals.
              </p>
              <button 
                onClick={() => navigate('/buy-chips')} 
                className="mt-2 px-4 py-1 bg-gradient-to-r from-violet-500 to-pink-500 rounded-md text-xs sm:text-sm text-white"
              >
                Go to Store
              </button>
            </div>
          </div>
        )}

        {/* Balance Panel */}
        <div className="p-1 rounded-lg bg-gradient-to-br from-gray-900 to-black border border-gray-700 shadow-xl mb-4 sm:mb-6">
          <div className="p-4 sm:p-6">
            <div className="flex flex-col items-center justify-between">
              <div className="mb-4 w-full">
                <div className="text-xs text-gray-400 uppercase tracking-wider mb-1 text-center">Your Balance</div>
                <div className="relative">
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-violet-500 to-pink-500 rounded-lg opacity-70 blur" />
                  <div className="relative px-6 py-2 sm:py-3 bg-gradient-to-br from-gray-900 to-black border border-gray-700 rounded-lg flex flex-col items-center">
                    <BanknotesIcon className="h-5 w-5 sm:h-6 sm:w-6 text-pink-400 mb-1" />
                    <div className="font-bold text-xl sm:text-2xl text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
                      {profile ? profile.chips.toLocaleString() : "0"}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="w-full">
                <div className="text-center bg-black bg-opacity-40 border border-gray-700 rounded-lg py-2 px-3 sm:px-4">
                  <div className="text-xs sm:text-sm font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
                    1 Chip = $0.001 USDT
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Swap Panel */}
        <div className={`p-1 rounded-lg bg-gradient-to-br from-gray-900 to-black border border-gray-700 shadow-xl mb-4 sm:mb-6 ${!eligibleForWithdrawal ? 'opacity-70' : ''}`}>
          <div className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h2 className="text-lg sm:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-500 via-pink-500 to-red-500">
                Swap Chips for USDT
              </h2>
              <div className="h-px flex-grow ml-2 sm:ml-4 bg-gradient-to-r from-violet-500/50 via-pink-500/50 to-transparent" />
            </div>

            {/* Purchase Status */}
            {profile && (
              <>
                {(profile.processedTransactions && profile.processedTransactions.length > 0) || 
                 (profile.recentActivity && profile.recentActivity.some(activity => activity.type === 'purchase')) ? (
                  <div className="mb-4 p-2 sm:p-3 bg-green-950 border border-green-500 rounded-lg flex items-start">
                    <CheckIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-xs sm:text-sm text-green-200">
                      {profile.recentActivity?.filter(activity => activity.type === 'purchase').length > 0 ? (
                        `You have made ${profile.recentActivity.filter(activity => activity.type === 'purchase').length} chip ${
                          profile.recentActivity.filter(activity => activity.type === 'purchase').length === 1 ? 'purchase' : 'purchases'
                        }. Withdrawals are enabled.`
                      ) : (
                        `You have made ${profile.processedTransactions.length} chip ${
                          profile.processedTransactions.length === 1 ? 'purchase' : 'purchases'
                        }. Withdrawals are enabled.`
                      )}
                    </span>
                  </div>
                ) : null}
              </>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-2 sm:p-3 bg-red-950 border border-red-500 rounded-lg flex items-start">
                <ExclamationCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-xs sm:text-sm text-red-200">{error}</span>
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="mb-4 p-2 sm:p-3 bg-green-950 border border-green-500 rounded-lg flex items-start">
                <CheckIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-xs sm:text-sm text-green-200">{success}</span>
              </div>
            )}

            {/* Chip Amount Input */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs text-gray-400 uppercase">Chip Amount</div>
                <button 
                  onClick={handleMaxClick}
                  className="text-xs text-pink-400 hover:text-pink-300 transition-colors"
                  disabled={!eligibleForWithdrawal}
                >
                  Max: {profile ? profile.chips.toLocaleString() : "0"}
                </button>
              </div>
              <div className="relative">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-violet-500 to-pink-500 rounded-lg opacity-30 blur" />
                <div className="relative flex items-center border border-gray-700 bg-gray-900 rounded-lg overflow-hidden">
                  <input
                    type="number"
                    value={chipAmount}
                    onChange={(e) => setChipAmount(e.target.value)}
                    placeholder="Enter amount"
                    className="flex-grow py-2 sm:py-3 px-3 sm:px-4 bg-transparent text-white outline-none text-sm w-full"
                    min="10000"
                    max={profile ? profile.chips : 0}
                    disabled={!eligibleForWithdrawal}
                  />
                  <div className="px-2 sm:px-4 py-2 sm:py-3 bg-black text-pink-400 text-sm">CHIPS</div>
                </div>
              </div>
              <div className="mt-2 text-xs text-gray-400">
                Minimum: 10000 chips
              </div>
            </div>

            {/* Conversion Arrow */}
            <div className="flex items-center justify-center my-3 sm:my-4">
              <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-r from-violet-500 to-pink-500 flex items-center justify-center">
                <ArrowRightIcon className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
              </div>
            </div>

            {/* USDT Amount Display */}
            <div className="mb-4 sm:mb-6">
              <div className="text-xs text-gray-400 uppercase mb-2">You Will Get</div>
              <div className="relative">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-violet-500 to-pink-500 rounded-lg opacity-30 blur" />
                <div className="relative flex items-center border border-gray-700 bg-gray-900 rounded-lg overflow-hidden">
                  <input
                    type="text"
                    value={usdtAmount}
                    readOnly
                    className="flex-grow py-2 sm:py-3 px-3 sm:px-4 bg-transparent text-white outline-none text-sm overflow-x-auto"
                  />
                  <div className="px-2 sm:px-4 py-2 sm:py-3 bg-black text-pink-400 text-sm whitespace-nowrap">USDT</div>
                </div>
              </div>
              <div className="mt-2 text-xs text-gray-400">
                ≈ ${(usdtAmount * 0.9).toFixed(3)} USD (10% platform fee)
              </div>
            </div>

            {/* Wallet Address Input */}
            <div className="mb-4 sm:mb-6">
              <div className="text-xs text-gray-400 uppercase mb-2">Your Wallet Address</div>
              <div className="relative">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-violet-500 to-pink-500 rounded-lg opacity-30 blur" />
                <div className="relative flex items-center border border-gray-700 bg-gray-900 rounded-lg overflow-hidden">
                  <input
                    type="text"
                    value={walletAddress}
                    onChange={(e) => setWalletAddress(e.target.value)}
                    placeholder="Enter USDT (SOL) wallet address"
                    className="flex-grow py-2 sm:py-3 px-3 sm:px-4 bg-transparent text-white outline-none text-xs sm:text-sm"
                    disabled={!eligibleForWithdrawal}
                  />
                  <button
                    onClick={() => handleCopy(walletAddress)}
                    className="px-2 sm:px-4 py-2 sm:py-3 hover:bg-black transition-colors"
                    disabled={!walletAddress || !eligibleForWithdrawal}
                  >
                    {copied ? (
                      <CheckIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-400" />
                    ) : (
                      <ClipboardDocumentIcon className="h-4 w-4 sm:h-5 sm:w-5 text-pink-400" />
                    )}
                  </button>
                </div>
              </div>
              <div className="mt-2 text-xs text-gray-400">
                USDT tokens will be sent to this address
              </div>
            </div>

            {/* Swap Button */}
            <button
              onClick={handleSwap}
              disabled={isProcessing || !profile || profile.chips <= 0 || !eligibleForWithdrawal}
              className={`w-full py-3 sm:py-4 rounded-lg flex items-center justify-center space-x-2 font-bold text-base sm:text-lg transition-colors ${
                isProcessing || !profile || profile.chips <= 0 || !eligibleForWithdrawal
                  ? "bg-gray-800 text-gray-500 cursor-not-allowed"
                  : "bg-gradient-to-r from-violet-500 to-pink-500 hover:from-violet-600 hover:to-pink-600 text-white"
              }`}
            >
              {isProcessing ? (
                <>
                  <ArrowPathIcon className="h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
                  <span>Processing...</span>
                </>
              ) : !eligibleForWithdrawal ? (
                <span>Purchase Chips First</span>
              ) : (
                <span>Swap Now</span>
              )}
            </button>
            
            <div className="mt-2 sm:mt-3 text-xs text-center text-gray-400">
              Tokens will be sent to your wallet
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-6 sm:mt-8 flex justify-center">
          <button 
            onClick={() => navigate('/profile')} 
            className="px-6 sm:px-8 py-2 sm:py-3 bg-gradient-to-r from-gray-800 to-gray-900 rounded-lg flex items-center space-x-2 border border-gray-700 hover:border-pink-500 transition-colors"
          >
            <span className="text-base sm:text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-violet-400">
              BACK TO PROFILE
            </span>
          </button>
        </div>

        {/* Check Status Button */}
        <div className="mt-3 sm:mt-4 flex justify-center">
          <button 
            onClick={() => {
              loadUserSwaps();
              setShowStatusPopup(true);
            }} 
            className="px-3 sm:px-4 py-2 bg-gray-800 rounded-lg border border-gray-700 hover:border-pink-500 transition-colors text-gray-300 hover:text-white text-xs sm:text-sm"
          >
            Check Swap Status
          </button>
        </div>
        
        {/* Swap Status Popup */}
        {showStatusPopup && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-2 sm:p-4 z-50">
            <div className="bg-gray-900 rounded-lg max-w-lg w-full p-4 sm:p-6 max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg sm:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
                  Your Swap Requests
                </h3>
                <button
                  onClick={() => setShowStatusPopup(false)}
                  className="text-gray-500 hover:text-white"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              
              {loadingSwaps ? (
                <div className="py-6 sm:py-8 text-center">
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 border-2 border-violet-500 rounded-full relative flex items-center justify-center">
                    <div className="w-10 h-10 bg-gradient-to-r from-violet-500 via-pink-500 to-red-500 rounded-full animate-pulse" />
                    <div className="absolute inset-0 border-t-2 border-violet-500 rounded-full animate-spin" />
                  </div>
                  <div className="mt-4 font-mono text-sm">
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
                      Loading Swap...
                    </span>
                  </div>
                </div>
              </div>
              ) : userSwaps.length === 0 ? (
                <div className="py-6 sm:py-8 text-center text-gray-400 text-sm">
                  <p>You don't have any swap requests yet.</p>
                </div>
              ) : (
                <div className="space-y-3 sm:space-y-4">
                  {userSwaps.map((swap, index) => {
                    // Match against your User model's swapRequestSchema
                    const status = swap.status || 'pending';
                    const chipAmount = swap.chipAmount || 0;
                    // Use pumpkinAmount as USDT amount (since that's what your model has)
                    const usdtAmount = (swap.pumpkinAmount !== undefined) 
                      ? Number(swap.pumpkinAmount) 
                      : (chipAmount * 0.001);
                    const walletAddress = swap.walletAddress || 'N/A';
                    const createdAt = swap.createdAt ? new Date(swap.createdAt) : new Date();
                    
                    return (
                      <div 
                        key={swap._id || index} 
                        className="p-3 sm:p-4 rounded-lg border border-gray-700 bg-gray-800"
                      >
                        <div className="flex justify-between mb-2 flex-wrap gap-2">
                          <div className="text-gray-300 text-xs">
                            {createdAt.toLocaleString()}
                          </div>
                          <div className={`px-2 py-1 rounded-full text-xs ${
                            status === 'pending' 
                              ? 'bg-yellow-600/30 text-yellow-300' 
                              : status === 'completed' 
                                ? 'bg-green-600/30 text-green-300'
                                : 'bg-red-600/30 text-red-300'
                          }`}>
                            {status}
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2 sm:gap-4 mb-2">
                          <div>
                            <div className="text-xs text-gray-500">Chips</div>
                            <div className="text-white text-sm">{chipAmount}</div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500">USDT</div>
                            <div className="text-white text-sm">
                              {typeof usdtAmount === 'number' 
                                ? usdtAmount.toFixed(3) 
                                : parseFloat(usdtAmount).toFixed(3)}
                            </div>
                          </div>
                        </div>
                        
                        <div>
                          <div className="text-xs text-gray-500">Wallet Address</div>
                          <div className="text-xs font-mono text-gray-300 truncate break-all">
                            {walletAddress}
                          </div>
                        </div>
                        
                        {status === 'pending' && (
                          <div className="mt-3 p-2 bg-yellow-900/20 border border-yellow-900 rounded text-xs text-yellow-300">
                            Your swap request is being processed. Please wait.
                          </div>
                        )}
                        
                        {status === 'completed' && (
                          <div className="mt-3 p-2 bg-green-900/20 border border-green-900 rounded text-xs text-green-300">
                            Your USDT tokens have been sent to your wallet.
                          </div>
                        )}
                        
                        {status === 'failed' && (
                          <div className="mt-3 p-2 bg-red-900/20 border border-red-900 rounded text-xs text-red-300">
                            Your swap request failed. Please contact support.
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              
              <div className="mt-4 flex justify-center">
                <button
                  onClick={() => loadUserSwaps()}
                  className="px-3 sm:px-4 py-2 bg-gray-800 rounded-lg border border-gray-700 hover:border-pink-500 transition-colors text-gray-300 hover:text-white text-xs sm:text-sm flex items-center space-x-1"
                >
                  <ArrowPathIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                  <span>Refresh</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
